// Event Program Display Component
// File: guest-frontend/src/components/EventProgram.js

import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import axios from 'axios';
import './EventProgram.css';
import { API_BASE_URL } from '../utils/apiBase';

export default function EventProgram({ eventId, eventConfig }) {
  const [programs, setPrograms] = useState([]);
  const [programMeta, setProgramMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [nowTs, setNowTs] = useState(Date.now());
  const itemRefs = useRef([]);

  const convertTime12To24 = (t) => {
    if (!t || typeof t !== 'string') return '';
    const m = t.trim().match(/^(\d{1,2}):(\d{2})\s*([AP]M)$/i);
    if (!m) return t; // maybe already 24h
    let h = parseInt(m[1],10);
    const min = m[2];
    const ampm = m[3].toUpperCase();
    if (ampm === 'AM') {
      if (h === 12) h = 0;
    } else if (ampm === 'PM') {
      if (h < 12) h += 12;
    }
    return `${String(h).padStart(2,'0')}:${min}`;
  };

  const normalizeItems = (rawItems) => {
    if (!Array.isArray(rawItems)) return [];
    return rawItems.map((it, idx) => {
      const start_time = it.start_time || convertTime12To24(it.time || '');
      let description = it.description;
      if (!description && Array.isArray(it.details)) {
        description = it.details.join('\n');
      }
      return {
        id: it.id || idx,
        title: it.title || it.name || `Item ${idx+1}`,
        start_time,
        duration_minutes: it.duration_minutes || it.duration || null,
        description,
        date: it.date || null,
      };
    });
  };

  const loadEventPrograms = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(`${API_BASE_URL}/events/${eventId}/program`);
      const data = response.data;
      if (data && typeof data === 'object' && (data.meta || data.items)) {
        setProgramMeta(data.meta || null);
        const baseItems = Array.isArray(data.items) ? data.items : [];
        setPrograms(baseItems);
        // Fallback: if no schedule_data but program_content has items/time
        if ((baseItems.length === 0) && data.meta?.program_content) {
          try {
            const pcRaw = data.meta.program_content;
            let obj = pcRaw;
            if (typeof pcRaw === 'string') {
              try { obj = JSON.parse(pcRaw); } catch { /* ignore */ }
            }
            if (obj && typeof obj === 'object' && Array.isArray(obj.items)) {
              const norm = normalizeItems(obj.items);
              if (norm.length) setPrograms(norm);
            }
          } catch { /* ignore */ }
        }
      } else if (Array.isArray(data)) {
        const meta = data[0] || null;
        setProgramMeta(meta);
        const sd = meta?.schedule_data;
        const items = Array.isArray(sd) ? sd : (sd && Array.isArray(sd.items) ? sd.items : []);
        let chosen = items;
        if (chosen.length === 0 && meta?.program_content) {
          try {
            let obj = meta.program_content;
            if (typeof obj === 'string') { try { obj = JSON.parse(obj); } catch { /* */ } }
            if (obj && typeof obj === 'object' && Array.isArray(obj.items)) {
              chosen = normalizeItems(obj.items);
            }
          } catch { /* ignore */ }
        }
        setPrograms(chosen);
      } else if (data && typeof data === 'object') {
        setProgramMeta(data);
        const sd = data.schedule_data;
        const items = Array.isArray(sd) ? sd : (sd && Array.isArray(sd?.items) ? sd.items : []);
        let chosen = items;
        if (chosen.length === 0 && data.program_content) {
          try {
            let obj = data.program_content;
            if (typeof obj === 'string') { try { obj = JSON.parse(obj); } catch { /* */ } }
            if (obj && typeof obj === 'object' && Array.isArray(obj.items)) {
              chosen = normalizeItems(obj.items);
            }
          } catch { /* ignore */ }
        }
        setPrograms(chosen);
      } else {
        setPrograms([]);
      }
    } catch (error) {
      console.error('Failed to load event programs:', error);
      setError('Unable to load event program. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    if (eventId) {
      loadEventPrograms();
    } else {
      setLoading(false);
      setPrograms([]);
      setProgramMeta(null);
    }
  }, [eventId, loadEventPrograms]);

  useEffect(() => {
    const id = setInterval(() => setNowTs(Date.now()), 30000);
    return () => clearInterval(id);
  }, []);

  // loadEventPrograms moved above and memoized

  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    try {
      return new Date(`2000-01-01T${timeStr}`).toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } catch {
      return timeStr;
    }
  };

  const formatDuration = (minutes) => {
    if (!minutes) return '';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    
    if (hours > 0 && mins > 0) {
      return `${hours}h ${mins}m`;
    } else if (hours > 0) {
      return `${hours}h`;
    } else {
      return `${mins}m`;
    }
  };

  const composeDateTime = (dateStr, hhmm) => {
    const base = (dateStr || programMeta?.event_date || eventConfig?.event_date || '').trim?.() || '';
    const d = base && !isNaN(Date.parse(base)) ? new Date(base) : new Date();
    const [h, m] = (hhmm || '').split(':').map((x)=>parseInt(x,10));
    if (!Number.isFinite(h) || !Number.isFinite(m)) return NaN;
    return new Date(d.getFullYear(), d.getMonth(), d.getDate(), h, m, 0, 0).getTime();
  };

  const enriched = useMemo(() => {
    return (programs || []).map((p, idx) => {
      const start = composeDateTime(p.date, p.start_time);
      const end = Number.isFinite(start) ? start + ((Number(p.duration_minutes)||0) * 60000) : NaN;
      const isCurrent = Number.isFinite(start) && Number.isFinite(end) && nowTs >= start && nowTs < end;
      return { ...p, _idx: idx, _start: start, _end: end, _isCurrent: isCurrent };
    });
  }, [programs, nowTs, programMeta, eventConfig?.event_date]);

  const currentIndex = useMemo(() => {
    const idx = enriched.findIndex(p => p._isCurrent);
    if (idx >= 0) return idx;
    // If none current, pick the next upcoming
    const now = nowTs;
    const future = enriched
      .filter(p => Number.isFinite(p._start) && p._start >= now)
      .sort((a,b) => a._start - b._start);
    return future.length ? future[0]._idx : -1;
  }, [enriched, nowTs]);

  const jumpToNow = () => {
    if (currentIndex < 0) return;
    const el = itemRefs.current[currentIndex];
    if (el && el.scrollIntoView) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  if (loading) {
    return (
      <div className="event-program">
        <div className="program-header">
          <h2>Event Program</h2>
        </div>
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading program...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="event-program">
        <div className="program-header">
          <h2>Event Program</h2>
        </div>
        <div className="error-state">
          <p>{error}</p>
          <button onClick={loadEventPrograms} className="retry-button">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (programs.length === 0) {
    return (
      <div className="event-program">
        <div className="program-header">
          <h2>Event Program</h2>
        </div>
        {!eventId ? (
          <div className="no-program">
            <p>Select an event to view the program.</p>
          </div>
        ) : programMeta ? (
          (() => {
            // Unwrap program_content: it may be a raw string or a JSON value string
            let html = '';
            const pc = programMeta.program_content;
            if (typeof pc === 'string') {
              try {
                // If it is a quoted JSON string, parse to the raw HTML
                const parsed = JSON.parse(pc);
                html = typeof parsed === 'string' ? parsed : pc;
              } catch (_) {
                html = pc;
              }
            } else if (pc && typeof pc === 'object') {
              // If stored as JSON object with value, attempt common keys
              html = pc.html || pc.content || '';
            }
            if (!html && (programMeta.html || programMeta.content)) {
              html = programMeta.html || programMeta.content;
            }
              // If a full HTML document was provided, extract <body> contents for embedding
              if (html) {
                try {
                  // Remove DOCTYPE to avoid odd rendering in innerHTML
                  html = html.replace(/<!DOCTYPE[^>]*>/i, '').trim();
                  const bodyMatch = html.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
                  if (bodyMatch && bodyMatch[1]) {
                    html = bodyMatch[1];
                  }
                } catch (_) { /* keep original */ }
              }
            return html ? (
              <div className="program-content" dangerouslySetInnerHTML={{ __html: html }} />
            ) : (
              <div className="no-program">
                <p>The event program will be available soon.</p>
                <div style={{ marginTop: 16 }}>
                  <button onClick={loadEventPrograms} className="retry-button">
                    Refresh
                  </button>
                </div>
              </div>
            );
          })()
  ) : (
          <div className="no-program">
            <p>The event program will be available soon.</p>
            <div style={{ marginTop: 16 }}>
              <button onClick={loadEventPrograms} className="retry-button">Refresh</button>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="event-program">
      <div className="program-header">
    <h2>{eventConfig?.eventTitle || 'Event'} Program</h2>
    {eventConfig?.event_date && (
          <p className="event-date">
      {new Date(eventConfig.event_date).toLocaleDateString('en-US', {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })}
          </p>
        )}
      </div>

      <div className="timeline-header">
        <button className="jump-now" onClick={jumpToNow} title="Jump to Now">⏱ Jump to Now</button>
      </div>

      <div className="program-timeline">
        {enriched.map((program, i) => (
          <div
            key={program.id ?? i}
            className={`program-item ${program._isCurrent ? 'current' : ''}`}
            ref={el => itemRefs.current[i] = el}
          >
            <div className="program-time">
              {program.start_time && (
                <div className="start-time">
                  {formatTime(program.start_time)}
                </div>
              )}
              {program.duration_minutes && (
                <div className="duration">
                  {formatDuration(program.duration_minutes)}
                </div>
              )}
            </div>
            
            <div className="program-content">
              <h3 className="program-title">{program.title}</h3>
              {program.description && (
                <p className="program-description">{program.description}</p>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="program-footer">
        <p>Program subject to change. Please listen for announcements.</p>
      </div>
    </div>
  );
}
